import { Injectable, NgZone, inject } from '@angular/core';
import * as PIXI from 'pixi.js';
import { PixelateFilter } from '@pixi/filter-pixelate';
import { BackgroundSegmentationService } from './background-segmentation.service';
import { BehaviorSubject } from 'rxjs';
import { Loggable } from 'app/utils/logging/loggable';
import { LogService } from 'app/services/logging.service';
import { FaceLandmarksService } from './face-landmarks.service';

export enum FilterType {
  NONE = 'none',
  BLUR = 'blur',
  PIXELATE = 'pixelate',
  GRAYSCALE = 'grayscale',
  SEPIA = 'sepia',
  INVERT = 'invert',
  HUE_ROTATE = 'hueRotate',
  BRIGHTNESS = 'brightness',
  CONTRAST = 'contrast',
  VINTAGE = 'vintage',
  NIGHT_VISION = 'nightVision',
  // Additional filter types
  VIGNETTE = 'vignette',
  GLITCH = 'glitch',
  DUOTONE = 'duotone',
  COLOR_SHIFT = 'colorShift',
  // Background-only filters
  BACKGROUND_BLUR = 'backgroundBlur',
  BACKGROUND_REPLACE = 'backgroundReplace',
  // Face filters using landmarks detection
  FACE_MASK = 'faceMask',
  FACE_HORNS = 'faceHorns',
  FACE_HALO = 'faceHalo',
}

/**
 * Service for managing video filters and effects using Pixi.js filters
 */
@Injectable({
  providedIn: 'root',
})
export class VideoFilterService extends Loggable {
  private ngZone = inject(NgZone);
  private backgroundSegmentationService = inject(BackgroundSegmentationService);
  private faceLandmarksService = inject(FaceLandmarksService);
  private logService = inject(LogService);

  // Cache for created filters to avoid recreating them
  private filterCache = new Map<string, PIXI.Filter>();

  // Track active segmentation
  private isBackgroundSegmentationActive = false;
  private isFaceDetectionActive = false;
  private segmentationRunning$ = new BehaviorSubject<boolean>(false);

  // Animation frame for mask updating
  private animationFrameId?: number;

  public constructor() {
    super();
    this.setDisplayName('VideoFilterService');
  }

  /**
   * Public getter to check if the segmentation loop is active.
   */
  public get isSegmentationRunning(): boolean {
    return this.isBackgroundSegmentationActive;
  }

  /**
   * Apply a filter to a sprite
   * @param sprite The sprite to apply the filter to
   * @param filterType The type of filter to apply
   * @param intensity Optional intensity parameter (0-1)
   * @returns The applied filter
   */
  public applyFilter(sprite: PIXI.Sprite, filterType: FilterType, intensity = 0.5): PIXI.Filter | null {
    // Stop segmentation if it's running and we are applying a non-background filter
    if (this.isBackgroundSegmentationActive) {
      this.logService.debug('VFS: Applying non-background filter, stopping segmentation loop.');
      this.stopSegmentationLoop();
    }

    // Remove existing filters before applying the new one
    sprite.filters = [];

    if (filterType === FilterType.NONE) {
      return null;
    }

    // Get or create the filter
    const filter = this.getFilter(filterType, intensity);
    if (filter) {
      // Apply the filter to the sprite
      sprite.filters = [filter];
    }

    return filter;
  }

  /**
   * Check if a filter type requires background segmentation or is a face filter.
   * @param filterType The filter type to check.
   * @returns True if it's a background filter or face filter, false otherwise.
   */
  public isBackgroundFilter(filterType: FilterType): boolean {
    return (
      filterType === FilterType.BACKGROUND_BLUR ||
      filterType === FilterType.BACKGROUND_REPLACE ||
      filterType === FilterType.FACE_MASK ||
      filterType === FilterType.FACE_HORNS ||
      filterType === FilterType.FACE_HALO
    );
  }

  /**
   * Check if a filter type is a face filter.
   * @param filterType The filter type to check.
   * @returns True if it's a face filter, false otherwise.
   */
  public isFaceFilter(filterType: FilterType): boolean {
    return (
      filterType === FilterType.FACE_MASK || filterType === FilterType.FACE_HORNS || filterType === FilterType.FACE_HALO
    );
  }

  /**
   * Apply multiple filters to a sprite
   * @param sprite The sprite to apply filters to
   * @param filterTypes Array of filter types to apply
   * @param intensities Optional array of intensities (0-1)
   * @returns Array of applied filters
   */
  public applyMultipleFilters(
    sprite: PIXI.Sprite,
    filterTypes: FilterType[],
    intensities: number[] = [],
  ): (PIXI.Filter | null)[] {
    // Remove existing filters
    sprite.filters = [];

    if (filterTypes.length === 0) {
      return [];
    }

    const filters: (PIXI.Filter | null)[] = [];
    const appliedFilters: PIXI.Filter[] = [];

    // Apply each filter
    for (let i = 0; i < filterTypes.length; i++) {
      const filterType = filterTypes[i];
      const intensity = intensities[i] !== undefined ? intensities[i] : 0.5;

      if (filterType !== FilterType.NONE) {
        const filter = this.getFilter(filterType, intensity);
        if (filter) {
          filters.push(filter);
          appliedFilters.push(filter);
        } else {
          filters.push(null);
        }
      } else {
        filters.push(null);
      }
    }

    // Apply all filters to the sprite
    if (appliedFilters.length > 0) {
      sprite.filters = appliedFilters;
    }

    return filters;
  }

  /**
   * Get or create a filter of the specified type
   * @param filterType The type of filter to get
   * @param intensity The intensity of the filter effect (0-1)
   * @returns The created or cached filter
   */
  private getFilter(filterType: FilterType, intensity: number): PIXI.Filter | null {
    // Create a cache key based on filter type and intensity
    const cacheKey = `${filterType}_${intensity.toFixed(2)}`;

    // Check if filter is already in cache
    if (this.filterCache.has(cacheKey)) {
      return this.filterCache.get(cacheKey)!;
    }

    // Create a new filter based on the type
    let filter: PIXI.Filter | null = null;

    switch (filterType) {
      case FilterType.BLUR:
        filter = new PIXI.BlurFilter(intensity * 10);
        break;

      case FilterType.PIXELATE:
        filter = new PixelateFilter(Math.max(2, Math.floor(intensity * 20)));
        break;

      case FilterType.GRAYSCALE:
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).greyscale(intensity, true);
        break;

      case FilterType.SEPIA:
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).sepia(true);
        break;

      case FilterType.INVERT:
        filter = new PIXI.ColorMatrixFilter();
        if (intensity > 0.5) {
          (filter as PIXI.ColorMatrixFilter).negative(true);
        }
        break;

      case FilterType.HUE_ROTATE:
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).hue(intensity * 360, true);
        break;

      case FilterType.BRIGHTNESS:
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).brightness(0.5 + intensity, true);
        break;

      case FilterType.CONTRAST:
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).contrast(0.5 + intensity, true);
        break;

      case FilterType.VINTAGE:
        // Vintage effect combines sepia and contrast
        filter = new PIXI.ColorMatrixFilter();
        (filter as PIXI.ColorMatrixFilter).sepia(true);
        (filter as PIXI.ColorMatrixFilter).contrast(1.2, false);
        (filter as PIXI.ColorMatrixFilter).brightness(0.8, false);
        break;

      case FilterType.NIGHT_VISION: {
        // Night vision effect combines green tint and noise
        filter = new PIXI.ColorMatrixFilter();
        // Create the specific readonly tuple
        const matrixConst = [0.1, 0.7, 0.1, 0, 0, 0.1, 0.7, 0.1, 0, 0, 0.1, 0.7, 0.1, 0, 0, 0, 0, 0, 1, 0] as const;
        // Create a mutable copy using the spread syntax before assigning
        (filter as PIXI.ColorMatrixFilter).matrix = [...matrixConst];
        break;
      }

      case FilterType.VIGNETTE: {
        // Vignette effect darkens the edges of the image
        const fragmentShader = `
          varying vec2 vTextureCoord;
          uniform sampler2D uSampler;
          uniform float uIntensity;
          
          void main(void) {
            vec4 color = texture2D(uSampler, vTextureCoord);
            
            // Calculate distance from center (0.5, 0.5)
            vec2 center = vec2(0.5, 0.5);
            float dist = distance(vTextureCoord, center);
            
            // Apply vignette effect based on distance and intensity
            float vignette = smoothstep(0.8, 0.2, dist * (1.0 + uIntensity));
            
            // Mix original color with darkened version
            gl_FragColor = vec4(color.rgb * vignette, color.a);
          }
        `;

        filter = new PIXI.Filter(undefined, fragmentShader, {
          uIntensity: intensity * 1.5, // Adjust intensity for better effect
        });
        break;
      }

      case FilterType.GLITCH: {
        // Digital glitch/distortion effect
        const fragmentShader = `
          varying vec2 vTextureCoord;
          uniform sampler2D uSampler;
          uniform float uIntensity;
          uniform float uTime;
          
          float rand(vec2 co) {
            return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
          }
          
          void main(void) {
            vec2 position = vTextureCoord;
            float noise = rand(vec2(floor(position.y * 100.0), uTime));
            
            // Apply horizontal shift based on noise and intensity
            if (noise < uIntensity * 0.3) {
              position.x += (noise - 0.15) * 0.025;
            }
            
            // RGB shift
            float r = texture2D(uSampler, position + vec2(uIntensity * 0.02, 0.0)).r;
            float g = texture2D(uSampler, position).g;
            float b = texture2D(uSampler, position - vec2(uIntensity * 0.02, 0.0)).b;
            
            // Random color blocks
            if (noise < uIntensity * 0.05) {
              float blockNoise = rand(vec2(floor(position.y * 40.0), floor(position.x * 40.0)));
              r = g = b = blockNoise;
            }
            
            gl_FragColor = vec4(r, g, b, 1.0);
          }
        `;

        // Create random time value for the effect
        const currentTime = (Date.now() % 10000) / 1000;

        filter = new PIXI.Filter(undefined, fragmentShader, {
          uIntensity: intensity,
          uTime: currentTime,
        });

        // Update time uniform in animation frame
        const updateTimeUniform = () => {
          if (filter && filter.uniforms.uTime !== undefined) {
            filter.uniforms.uTime = (Date.now() % 10000) / 1000;
            requestAnimationFrame(updateTimeUniform);
          }
        };

        requestAnimationFrame(updateTimeUniform);
        break;
      }

      case FilterType.DUOTONE: {
        // Duotone effect maps luminance to a gradient between two colors
        const fragmentShader = `
          varying vec2 vTextureCoord;
          uniform sampler2D uSampler;
          uniform vec3 uDarkColor;
          uniform vec3 uLightColor;
          uniform float uIntensity;
          
          void main(void) {
            vec4 texColor = texture2D(uSampler, vTextureCoord);
            
            // Calculate luminance (brightness)
            float luminance = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
            
            // Create duotone color by interpolating between two colors based on luminance
            vec3 duotoneColor = mix(uDarkColor, uLightColor, luminance);
            
            // Mix original and duotone based on intensity
            vec3 finalColor = mix(texColor.rgb, duotoneColor, uIntensity);
            
            gl_FragColor = vec4(finalColor, texColor.a);
          }
        `;

        // Generate colors based on intensity
        // Intensity affects the hue of the light color
        const hue = intensity * 360.0;

        // Convert HSL to RGB (for light color)
        let lightR = 0,
          lightG = 0,
          lightB = 0;
        const darkR = 0.1,
          darkG = 0.1,
          darkB = 0.2; // Dark blue-ish

        // Simplified HSL to RGB conversion
        const h = hue / 60.0;
        const s = 0.7;
        const l = 0.7;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h % 2) - 1));
        const m = l - c / 2;

        if (h >= 0 && h < 1) {
          lightR = c;
          lightG = x;
          lightB = 0;
        } else if (h >= 1 && h < 2) {
          lightR = x;
          lightG = c;
          lightB = 0;
        } else if (h >= 2 && h < 3) {
          lightR = 0;
          lightG = c;
          lightB = x;
        } else if (h >= 3 && h < 4) {
          lightR = 0;
          lightG = x;
          lightB = c;
        } else if (h >= 4 && h < 5) {
          lightR = x;
          lightG = 0;
          lightB = c;
        } else {
          lightR = c;
          lightG = 0;
          lightB = x;
        }

        lightR += m;
        lightG += m;
        lightB += m;

        filter = new PIXI.Filter(undefined, fragmentShader, {
          uDarkColor: [darkR, darkG, darkB],
          uLightColor: [lightR, lightG, lightB],
          uIntensity: Math.min(1.0, 0.5 + intensity),
        });
        break;
      }

      case FilterType.COLOR_SHIFT: {
        // RGB channel shift effect
        const fragmentShader = `
          varying vec2 vTextureCoord;
          uniform sampler2D uSampler;
          uniform float uIntensity;
          uniform vec2 uRedShift;
          uniform vec2 uGreenShift;
          uniform vec2 uBlueShift;
          
          void main(void) {
            // Sample each color channel with different offsets
            float r = texture2D(uSampler, vTextureCoord + uRedShift).r;
            float g = texture2D(uSampler, vTextureCoord + uGreenShift).g;
            float b = texture2D(uSampler, vTextureCoord + uBlueShift).b;
            
            gl_FragColor = vec4(r, g, b, 1.0);
          }
        `;

        // Calculate channel shifts based on intensity
        const angle = intensity * Math.PI * 2.0;
        const dist = intensity * 0.03;

        // Create different shifts for each color channel
        const redShift = [Math.cos(angle) * dist, Math.sin(angle) * dist];
        const greenShift = [Math.cos(angle + 2.0) * dist, Math.sin(angle + 2.0) * dist];
        const blueShift = [Math.cos(angle + 4.0) * dist, Math.sin(angle + 4.0) * dist];

        filter = new PIXI.Filter(undefined, fragmentShader, {
          uIntensity: intensity,
          uRedShift: redShift,
          uGreenShift: greenShift,
          uBlueShift: blueShift,
        });
        break;
      }

      default:
        return null;
    }

    // Cache the filter for future use
    if (filter) {
      this.filterCache.set(cacheKey, filter);
    }

    return filter;
  }

  /**
   * Create a custom filter from a fragment shader
   * @param fragmentShader The fragment shader code
   * @param uniforms Custom uniforms for the shader
   * @returns The created filter
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createCustomFilter(fragmentShader: string, uniforms: any = {}): PIXI.Filter {
    return new PIXI.Filter(undefined, fragmentShader, uniforms);
  }

  /**
   * Clear all filters from a sprite
   * @param sprite The sprite to clear filters from
   */
  public clearFilters(sprite: PIXI.Sprite): void {
    sprite.filters = [];
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    // Destroy all cached filters
    this.filterCache.forEach((filter) => {
      filter.destroy();
    });

    this.filterCache.clear();
  }

  /**
   * Apply a filter only to the background of a video
   * THIS METHOD IS DEPRECATED - Use applyVideoFilter instead
   * Keeping this for backward compatibility
   * @param sprite The sprite to apply the background filter to
   * @param videoElement The video element containing the source
   * @param filterType The type of filter to apply
   * @param renderer The PIXI renderer instance
   * @param intensity Optional intensity parameter (0-1) - Note: This is passed to the underlying filters
   * @returns Promise resolving to the applied filter or null
   */
  public async applyBackgroundFilter(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    filterType: FilterType,
    renderer: PIXI.Renderer,
    intensity = 0.5,
  ): Promise<PIXI.Filter | null> {
    // Forward to the new method and pass intensity to maintain backward compatibility
    await this.applyVideoFilter(sprite, filterType, videoElement, renderer);

    // Return the filter for backward compatibility
    return sprite.filters?.[0] || null;
  }

  /**
   * Apply a background replacement effect
   * @param sprite The sprite to apply the effect to
   * @param videoElement The video element containing the source
   * @param intensity Optional color intensity for solid backgrounds
   * @returns The applied filter or null
   */
  private async applyBackgroundReplacement(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    renderer: PIXI.Renderer,
    intensity = 0.5,
  ): Promise<PIXI.Filter | null> {
    this.logService.info('VFS: Creating background replacement filter.');
    const fragmentShader = `
      varying vec2 vTextureCoord;
      uniform sampler2D uSampler;
      uniform sampler2D uMaskSampler;
      uniform vec3 uBackgroundColor;
      uniform float uFeatherSize;
      
      void main(void) {
        vec4 originalColor = texture2D(uSampler, vTextureCoord);
        vec4 maskColor = texture2D(uMaskSampler, vTextureCoord);
        float maskValue = maskColor.r;
        float featherFactor = smoothstep(0.0, uFeatherSize, maskValue);
        vec4 backgroundColor = vec4(uBackgroundColor, originalColor.a);
        gl_FragColor = mix(backgroundColor, originalColor, featherFactor);
      }
    `;

    // Create gradient colors based on intensity
    // Higher intensity means more vibrant colors
    const hue = intensity * 360.0; // Use intensity to pick a hue from the color wheel

    // Convert HSL to RGB (simple conversion for demonstration)
    const h = hue / 60.0;
    const s = 0.7; // saturation
    const l = 0.5; // lightness

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;

    if (h >= 0 && h < 1) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 1 && h < 2) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 2 && h < 3) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 3 && h < 4) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 4 && h < 5) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }
    r = r + m;
    g = g + m;
    b = b + m;

    this.logService.info('VFS: Attempting to get initial mask texture for replacement...');
    const initialMaskTexture = this.backgroundSegmentationService.updateMaskTexture(new ImageData(1, 1));

    if (!initialMaskTexture) {
      this.logService.error('VFS: Could not get initial mask texture for background replacement');
      return null;
    }
    this.logService.info('VFS: Initial mask texture obtained for replacement.');

    // Create a custom filter with the background replacement shader
    const filter = new PIXI.Filter(undefined, fragmentShader, {
      // Initialize with the texture from the service
      uMaskSampler: initialMaskTexture,
      uBackgroundColor: [r, g, b],
      uFeatherSize: 0.05, // Edge feathering amount
    });

    // Activate segmentation service
    this.logService.info('VFS: Activating segmentation service for replacement.');
    this.backgroundSegmentationService.activate();

    // Start the segmentation process
    this.logService.info('VFS: Starting segmentation loop for replacement.');
    await this.startSegmentationLoop(sprite, videoElement, filter, renderer);

    return filter;
  }

  /**
   * Start a continuous segmentation loop for background effects
   * @param sprite The sprite to apply effects to
   * @param videoElement The video element to segment
   * @param filter The filter to apply to the background
   */
  private async startSegmentationLoop(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    filter: PIXI.Filter,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    this.logService.info('VFS: Inside startSegmentationLoop.');

    // Set the flag to indicate segmentation is active
    this.isBackgroundSegmentationActive = true;
    this.segmentationRunning$.next(true);

    this.logService.info('VFS: Performing initial frame process.');
    await this.processVideoFrame(sprite, videoElement, filter, renderer);

    // Start the loop for continuous updates
    const updateSegmentation = async () => {
      if (!this.isBackgroundSegmentationActive) {
        this.logService.info('VFS: Segmentation loop stopped (detected inside animation frame).');
        return;
      }
      this.logService.info('VFS: Animation frame tick - processing video frame.');
      await this.processVideoFrame(sprite, videoElement, filter, renderer);
      this.animationFrameId = requestAnimationFrame(updateSegmentation);
    };

    this.logService.info('VFS: Starting requestAnimationFrame loop.');
    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(updateSegmentation);
    });
  }

  /**
   * Process a single video frame for segmentation
   * @param sprite The sprite to apply the effect to
   * @param videoElement The video element to process
   * @param filter The filter to apply
   */
  private async processVideoFrame(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    filter: PIXI.Filter,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    try {
      if (videoElement.paused || videoElement.ended) {
        return;
      }

      // Log the start of processing for debugging
      this.logService.info('VFS: Processing video frame...');

      const segmentation = await this.backgroundSegmentationService.segmentFrame(videoElement);
      if (!segmentation) {
        this.logService.warn('VFS: No segmentation returned');
        return;
      }

      // Log segmentation stats
      let minVal = 255,
        maxVal = 0,
        sum = 0;
      for (let i = 0; i < segmentation.data.length; i += 4) {
        minVal = Math.min(minVal, segmentation.data[i]);
        maxVal = Math.max(maxVal, segmentation.data[i]);
        sum += segmentation.data[i];
      }
      const avg = sum / (segmentation.width * segmentation.height);
      this.logService.info(`VFS: Segmentation stats - Min: ${minVal}, Max: ${maxVal}, Avg: ${avg.toFixed(2)}`);

      const maskTexture = this.backgroundSegmentationService.updateMaskTexture(segmentation);
      if (!maskTexture) {
        this.logService.warn('VFS: Failed to update mask texture.');
        return;
      }

      // --- Update the background filter texture using the dedicated service method ---
      // This replaces the manual rendering logic that was here previously.
      this.backgroundSegmentationService.updateBackgroundFilter(sprite, filter, renderer);
      this.logService.info('VFS: Called backgroundSegmentationService.updateBackgroundFilter');

      // Update the mask texture uniform in the filter
      if (filter.uniforms.uMaskSampler) {
        filter.uniforms.uMaskSampler = maskTexture;
        this.logService.info('VFS: Updated mask texture in filter uniform');
      }

      // Ensure the composite filter is applied to the sprite
      if (!sprite.filters?.includes(filter)) {
        sprite.filters = [filter];
        this.logService.info('VFS: Applied composite filter to sprite');
      }
    } catch (error) {
      this.logService.error('VFS: Error processing video frame for segmentation', error);
    }
  }

  /**
   * Stop the segmentation loop
   */
  private stopSegmentationLoop(): void {
    this.logService.info('VFS: Stopping segmentation loop.');

    this.isBackgroundSegmentationActive = false;
    this.segmentationRunning$.next(false);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }

    // Deactivate the segmentation service when the loop stops
    this.backgroundSegmentationService.deactivate();
  }

  /**
   * Apply a face filter effect using facial landmark detection
   * @param sprite The sprite to apply the effect to
   * @param videoElement The video element to process
   * @param filterType The type of face filter to apply
   * @param renderer The PIXI renderer
   * @param intensity Optional intensity parameter (0-1)
   * @returns Promise resolving to true if successful
   */
  public async applyFaceFilter(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    filterType: FilterType,
    renderer: PIXI.Renderer,
    _intensity = 0.5,
  ): Promise<boolean> {
    this.logService.info(`VFS: Applying face filter: ${filterType}`);

    // Early return if no face filter needed
    if (filterType === FilterType.NONE) {
      this.logService.info('VFS: FilterType is NONE, stopping face detection and clearing overlays.');
      this.stopFaceDetectionLoop();
      return false;
    }

    // Activate the face landmarks service
    this.faceLandmarksService.activate();

    // Start the face detection loop with the specified filter type
    await this.startFaceDetectionLoop(sprite, videoElement, filterType.toString(), renderer);

    return true;
  }

  /**
   * Start a continuous face detection loop for face filters
   * @param sprite The sprite to apply effects to
   * @param videoElement The video element to process
   * @param filterType The type of face filter to apply
   * @param renderer The PIXI renderer
   */
  private async startFaceDetectionLoop(
    sprite: PIXI.Sprite,
    videoElement: HTMLVideoElement,
    filterType: string,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    this.logService.info('VFS: Starting face detection loop.');

    // Set the flag to indicate face detection is active
    this.isFaceDetectionActive = true;

    // Start the detection loop with the face landmarks service
    await this.faceLandmarksService.startDetectionLoop(videoElement, sprite, filterType);
  }

  /**
   * Stop the face detection loop
   */
  private stopFaceDetectionLoop(): void {
    this.logService.info('VFS: Stopping face detection loop.');

    this.isFaceDetectionActive = false;

    // Deactivate the face landmarks service
    this.faceLandmarksService.deactivate();
  }

  /**
   * Remove filter from a sprite
   * @param sprite The sprite to remove filter from
   */
  public removeFilter(sprite: PIXI.Sprite): void {
    this.logService.debug('VFS: Removing filters from sprite');
    sprite.filters = [];
  }

  /**
   * Apply background blur filter
   * @param sprite The sprite to apply blur to
   * @param video The video element
   * @param renderer The PIXI renderer
   */
  private async applyBackgroundBlur(
    sprite: PIXI.Sprite,
    video: HTMLVideoElement,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    this.logService.info('VFS: Applying background blur');
    await this.applyBackgroundFilter(sprite, video, FilterType.BACKGROUND_BLUR, renderer, 0.5);
  }

  /**
   * Apply background replacement filter
   * @param sprite The sprite to apply replacement to
   * @param video The video element
   * @param renderer The PIXI renderer
   */
  private async applyBackgroundReplace(
    sprite: PIXI.Sprite,
    video: HTMLVideoElement,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    this.logService.info('VFS: Applying background replacement');
    await this.applyBackgroundFilter(sprite, video, FilterType.BACKGROUND_REPLACE, renderer, 0.5);
  }

  /**
   * Apply a filter to the video based on the filter type
   * Uses the appropriate filter handling based on filter type
   * @param sprite The sprite to apply the filter to
   * @param filterType The type of filter to apply
   * @param video The video element
   * @param renderer The PIXI renderer
   */
  public async applyVideoFilter(
    sprite: PIXI.Sprite,
    filterType: FilterType,
    video: HTMLVideoElement,
    renderer: PIXI.Renderer,
  ): Promise<void> {
    this.logService.info(`Applying filter: ${filterType}`);

    // First, remove any existing filter
    this.removeFilter(sprite);

    // Check if there's a face filter to apply
    if (this.isFaceFilter(filterType)) {
      this.logService.info(`Applying face filter: ${filterType}`);

      const filterTypeStr = this.getFilterTypeString(filterType);

      // Start face landmarks detection for the face filter
      this.faceLandmarksService.activate();
      void this.faceLandmarksService.startDetectionLoop(video, sprite, filterTypeStr);

      return;
    }

    // Handle background filters
    if (filterType === FilterType.BACKGROUND_BLUR) {
      await this.applyBackgroundBlur(sprite, video, renderer);
    } else if (filterType === FilterType.BACKGROUND_REPLACE) {
      await this.applyBackgroundReplace(sprite, video, renderer);
    } else {
      // For regular filters, use the standard applyFilter method
      this.applyFilter(sprite, filterType);
    }
  }

  /**
   * Convert FilterType enum to string representation for face filters
   * @param filterType The filter type enum
   * @returns String representation of the filter type
   */
  private getFilterTypeString(filterType: FilterType): string {
    switch (filterType) {
      case FilterType.FACE_MASK:
        return 'faceMask';
      case FilterType.FACE_HORNS:
        return 'faceHorns';
      case FilterType.FACE_HALO:
        return 'faceHalo';
      default:
        this.logService.warn(`Unknown face filter type: ${filterType}`);
        return 'faceMask'; // Default to mask if unknown
    }
  }
}
